import { request } from './httpClient';

/**
 * Marketplace inventory search (Cosmos DB via Tenant API). Does not call Salesforce.
 * @param {{ page?: number, page_size?: number, q?: string, stripped_q?: string }} params
 */
export function getMarketplaceCatalog(params) {
  return request({
    method: 'GET',
    url: '/v1/marketplace/catalog',
    params: {
      page: params.page ?? 1,
      page_size: params.page_size ?? 25,
      ...(params.q ? { q: params.q } : {}),
      ...(params.stripped_q ? { stripped_q: params.stripped_q } : {}),
    },
  });
}

export function getMarketplaceCatalogItem(itemId) {
  const enc = encodeURIComponent(itemId);
  return request({
    method: 'GET',
    url: `/v1/marketplace/catalog/item/${enc}`,
  });
}

export function patchCatalogItemQuantity(itemId, quantity) {
  const enc = encodeURIComponent(itemId);
  return request({
    method: 'PATCH',
    url: `/v1/marketplace/catalog/item/${enc}`,
    data: { quantity },
  });
}

export function getMarketplaceSources() {
  return request({ method: 'GET', url: '/v1/marketplace/sources' });
}

/**
 * Live pricing resolver (authenticated; forwards the buyer identity so CCP can be
 * scoped to their account). Returns a single resolved unit price per condition,
 * applying the precedence: line price → Price → VCP → CCP.
 * @returns {Promise<{ productId: string, currency?: string, sourcesConsidered?: string[], prices: Array<{ condition: string, price: number, source: string }> }>}
 */
export function getResolvedPricing(productId) {
  return request({
    method: 'GET',
    url: `/v1/pricing/${encodeURIComponent(productId)}`,
  });
}

export function getVcp(productId) {
  return request({
    method: 'GET',
    url: `/v1/vcp/${encodeURIComponent(productId)}`,
  });
}

export function getSecondaryInventory(productId) {
  return request({
    method: 'GET',
    url: `/v1/secondary-inventory/${encodeURIComponent(productId)}`,
  });
}

export function addToCart(item) {
  return request({
    method: 'POST',
    url: '/v1/marketplace/cart',
    data: item,
  });
}

export function getCart() {
  return request({ method: 'GET', url: '/v1/marketplace/cart' });
}

export function removeCartItem(itemId) {
  return request({
    method: 'DELETE',
    url: `/v1/marketplace/cart/${encodeURIComponent(itemId)}`,
  });
}

export function clearCart() {
  return request({ method: 'DELETE', url: '/v1/marketplace/cart' });
}

/** Atomically update the requested quantity of a cart line (ETag-guarded server-side). */
export function updateCartItemQuantity(itemId, quantity) {
  return request({
    method: 'PATCH',
    url: `/v1/marketplace/cart/${encodeURIComponent(itemId)}`,
    data: { quantity },
  });
}

export function submitCart(itemIds, quoteHeader = {}) {
  return request({
    method: 'POST',
    url: '/v1/marketplace/cart/submit',
    data: { item_ids: itemIds, quote_header: quoteHeader },
  });
}

/** Move a cart item between sections: shopping | rfq | saved_for_later */
export function updateCartItemSection(itemId, cartSection) {
  return request({
    method: 'PATCH',
    url: `/v1/marketplace/cart/${encodeURIComponent(itemId)}/section`,
    data: { cart_section: cartSection },
  });
}

/** Purchase shopping-cart items (creates Salesforce Sales Order). */
export function purchaseCart(itemIds, poNumber) {
  return request({
    method: 'POST',
    url: '/v1/marketplace/cart/purchase',
    data: { item_ids: itemIds, po_number: poNumber },
  });
}

/**
 * @param {{ page?: number, page_size?: number, q?: string, stripped_q?: string, ata?: string }} params
 */
export function getRepairCapabilitiesCatalog(params) {
  return request({
    method: 'GET',
    url: '/v1/marketplace/repair-capabilities',
    params: {
      page: params.page ?? 1,
      page_size: params.page_size ?? 25,
      ...(params.q ? { q: params.q } : {}),
      ...(params.stripped_q ? { stripped_q: params.stripped_q } : {}),
      ...(params.ata ? { ata: params.ata } : {}),
    },
  });
}

export function getRepairCapabilityItem(itemId) {
  return request({
    method: 'GET',
    url: `/v1/marketplace/repair-capabilities/item/${encodeURIComponent(itemId)}`,
  });
}

/** Returns { parts_catalog_offered, repair_capabilities_offered, repair_pricing_displayed } */
export function getCatalogOfferings() {
  return request({ method: 'GET', url: '/v1/marketplace/catalog-offerings' });
}

export function addRepairToCart(item) {
  return request({
    method: 'POST',
    url: '/v1/marketplace/cart',
    data: { catalog_kind: 'repair', ...item },
  });
}
